import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { formatDropTime, formatVnd } from "../../shared/i18n/index.ts";
import type { PublicListing } from "../../shared/types.ts";
import { AlertControl } from "../components/AlertControl.tsx";
import { BuySheet } from "../components/BuySheet.tsx";
import { Counts } from "../components/Counts.tsx";
import { EmptyState } from "../components/EmptyState.tsx";
import { PriceDisplay } from "../components/PriceDisplay.tsx";
import { ReportSheet } from "../components/ReportSheet.tsx";
import { TimeBucket } from "../components/TimeBucket.tsx";
import { api } from "../lib/api.ts";
import { serverNow } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { useSegment } from "../lib/segments.ts";

/** /l/:id — the listing page (08-design.md §Listing). Buy/Watch/Report wire up in Phase 3 / T2.9. */
export function Listing() {
  const { id = "" } = useParams();
  const { t, locale } = useI18n();
  const { me } = useMe();
  const [state, setState] = useState<
    { status: "loading" } | { status: "missing" } | { status: "ok"; p: PublicListing }
  >({ status: "loading" });
  const [slide, setSlide] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [buying, setBuying] = useState(false);
  const [counts, setCounts] = useState<{ watching: number; alerts: number } | null>(null);

  useEffect(() => {
    let live = true;
    api<PublicListing>(`/api/listings/${id}`)
      .then((p) => live && setState({ status: "ok", p }))
      .catch(() => live && setState({ status: "missing" }));
    return () => {
      live = false;
    };
  }, [id]);

  const p = state.status === "ok" ? state.p : null;
  const { seg } = useSegment(id, p?.segment ?? null);

  if (state.status === "loading") return <div className="page muted">{t("common.loading")}</div>;
  if (!p)
    return (
      <div className="page">
        <EmptyState icon="?" text={t("error.not_found")} cta={{ label: t("nav.home"), to: "/" }} />
      </div>
    );
  const s = seg ?? p.segment;
  const ended = s.status === "sold_out" || s.status === "expired" || s.status === "cancelled";
  const isOwner = me?.id === p.seller.id;
  const cityName = p.city ? (locale === "vi" ? p.city.name_vi : p.city.name_en) : "";
  const categoryName = p.category
    ? locale === "vi"
      ? p.category.name_vi
      : p.category.name_en
    : "";
  const hours = p.seller.median_share_s
    ? Math.max(1, Math.round(p.seller.median_share_s / 3600))
    : null;

  return (
    <div className="page page--narrow" data-testid="listing-page">
      {p.hidden ? <div className="banner banner--warn">{t("listing.under_review")}</div> : null}
      <div className="gallery">
        {p.images[slide] ? (
          <img className="hero" src={p.images[slide].url} alt="" />
        ) : (
          <div className="hero" />
        )}
        {p.images.length > 1 ? (
          <div className="gallery__dots">
            {p.images.map((img, i) => (
              <button
                key={img.url}
                className={`dot${i === slide ? " is-on" : ""}`}
                aria-label={String(i + 1)}
                onClick={() => setSlide(i)}
              />
            ))}
          </div>
        ) : null}
        <div className="tags">
          {p.condition ? (
            <span className="tag">{t(`listing.condition.${p.condition}`)}</span>
          ) : null}
          {p.is_drop ? <span className="tag tag--drop">{t("listing.drop_tag")}</span> : null}
        </div>
      </div>

      <h1 className="h1" data-testid="listing-title">
        {p.title}
      </h1>
      <p className="muted">
        {[categoryName, [p.district, cityName].filter(Boolean).join(", ")]
          .filter(Boolean)
          .join(" · ")}
      </p>

      <section className="pricebox" data-testid="pricebox">
        {ended ? (
          <>
            <p className="ended">
              {t(`listing.status.${s.status as "sold_out" | "expired" | "cancelled"}`)}
            </p>
            <Link className="btn" to="/s">
              {t("listing.similar")}
            </Link>
          </>
        ) : s.status === "scheduled" ? (
          <>
            <p className="price price--md price--far">
              <span className="price__value">{formatVnd(p.start_price)}</span>
            </p>
            <p className="muted">{t("listing.starts_at_drop", { when: t("drop.time_label") })}</p>
          </>
        ) : (
          <>
            <PriceDisplay segment={s} size="lg" startPrice={p.start_price} />
            <div className="row">
              <TimeBucket bucket={s.bucket} />
              <Counts
                watching={(counts ?? p.counts).watching}
                alerts={(counts ?? p.counts).alerts}
              />
              {p.quantity > 1 ? (
                <span className="muted small">
                  {t("listing.qty_remaining", { n: s.qty_remaining })}
                </span>
              ) : null}
            </div>
          </>
        )}
      </section>

      {!ended && s.status === "active" ? (
        <div className="actions">
          {isOwner ? (
            <Link className="btn btn--accent btn--block" to={`/me/listings/${p.id}`}>
              {t("nav.sell")}
            </Link>
          ) : (
            <>
              <button
                className="btn btn--accent btn--block"
                onClick={() => setBuying((b) => !b)}
                data-testid="buy"
              >
                {t("buy.cta")}
              </button>
              {buying ? (
                <BuySheet listingId={p.id} segment={s} onClose={() => setBuying(false)} />
              ) : null}
              <AlertControl listingId={p.id} currentPrice={s.price} onCounts={setCounts} />
            </>
          )}
          {me && !isOwner ? (
            <button
              className="btn btn--ghost"
              onClick={() => setReporting((r) => !r)}
              data-testid="report-open"
            >
              {t("listing.report")}
            </button>
          ) : null}
          {reporting ? <ReportSheet listingId={p.id} onClose={() => setReporting(false)} /> : null}
        </div>
      ) : null}

      <Link to={`/u/${p.seller.handle}`} className="sellercard" data-testid="seller-card">
        <span className="avatar">
          {(p.seller.display_name || p.seller.handle).slice(0, 2).toUpperCase()}
        </span>
        <span>
          <strong>@{p.seller.handle}</strong>
          <span className="hint">
            {t("profile.sold", { n: p.seller.completed_sales })}
            {hours !== null
              ? ` · ${t("purchase.usual_response", { hours }).toLowerCase()}`
              : ""} ·{" "}
            {t("common.since", { year: new Date(p.seller.member_since * 1000).getFullYear() })}
          </span>
        </span>
      </Link>

      {p.description ? <p className="description">{p.description}</p> : null}
      <p className="muted small">{t("price.rule")}</p>
      {p.status === "scheduled" ? (
        <p className="hint">{formatDropTime(serverNow(), locale)}</p>
      ) : null}
    </div>
  );
}
